import React, { useState } from "react";
import { Snackbar, Modal, Fade, Backdrop } from "@material-ui/core";
import MuiAlert, { AlertProps } from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";

import { ReactComponent as CancelOrder } from "../../../assets/cancel-order.svg";
import { ReactComponent as RemoveIcon } from "../../../assets/cross.svg";
import { ReactComponent as TrashIcon } from "./trash-icon.svg";

import Loader from "../../common/loader/loader.component";
import { useStyles } from "./CancelOrder.styles";
import { useCancelOrder } from "../hooks/useCancelOrder";

interface AccountCancelOrderProps {
  orderId: string;
  handleCloseParentModal?: () => void;
}

const Alert: React.FC<AlertProps> = (props) => (
  <MuiAlert elevation={6} variant="filled" {...props} />
);

const AccountCancelOrder: React.FC<AccountCancelOrderProps> = ({
  orderId,
  handleCloseParentModal,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { cancelOrder, loading } = useCancelOrder();

  const closeModal = () => setIsModalOpen(false);

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const handleCancelOrder = async () => {
    const result = await cancelOrder(orderId);

    if (result.ok) {
      // Success flow
      setSnackbar({
        open: true,
        message: t("itemPage.ItemEditMessage", {
          action: t("itemPage.cancelled"),
        }),
        severity: "success",
      });

      setTimeout(() => {
        closeModal();
        handleCloseParentModal?.();
      }, 500);
    } else {
      // Error flow
      setSnackbar({
        open: true,
        message: result.message,
        severity: "error",
      });
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <span
        className={classes.cancelTrigger}
        onClick={() => setIsModalOpen(true)}
      >
        <TrashIcon />
        {t("account.cancelOrder")}
      </span>

      {/* Modal */}
      <Modal
        className={classes.modal}
        open={isModalOpen}
        onClose={closeModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={isModalOpen}>
          <div className={classes.container}>
            {/* Header */}
            <div className={classes.header}>
              <div className={classes.headerTitles}>
                <span>{t("account.cancelThisOrder")}</span>
              </div>
              <div className={classes.closeIcon} onClick={closeModal}>
                <RemoveIcon style={{ width: 24, height: 24 }} />
              </div>
            </div>

            {/* Icon */}
            <CancelOrder />

            {/* Confirm Button */}
            <button
              className={classes.AccountCancelOrderButton}
              onClick={handleCancelOrder}
              disabled={loading}
            >
              {loading ? (
                <Loader color="#B73232" size="15" />
              ) : (
                t("account.cancelOrder")
              )}
            </button>

            {/* Not Now */}
            <span className={classes.notNow} onClick={closeModal}>
              {t("account.notNow")}
            </span>
          </div>
        </Fade>
      </Modal>

      {/* RESULT SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: t("css.snackBarPosition") as "left" | "right" | "center",
        }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AccountCancelOrder;
